import { useState } from 'react';
import { apiFetch } from '../api/client';
import { useMyRoleApplications, RoleApplication } from '../api/hooks';
import { useAuth } from '../auth/AuthContext';

const ORG_TYPES = ['Company', 'Non-profit', 'Government', 'Academic', 'Individual', 'Other'];
const ROLE_LABELS: Record<string, string> = { issuer: 'Credential Issuer', verifier: 'Credential Verifier' };

function StatusBadge({ status }: { status: string }) {
  const colors: Record<string, string> = {
    pending: 'bg-yellow-100 text-yellow-800',
    approved: 'bg-green-100 text-green-800',
    rejected: 'bg-red-100 text-red-800',
  };
  return (
    <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ${colors[status] ?? 'bg-slate-100 text-slate-700'}`}>
      {status.charAt(0).toUpperCase() + status.slice(1)}
    </span>
  );
}

function ApplicationCard({ app }: { app: RoleApplication }) {
  return (
    <div className="rounded-xl border border-border-subtle bg-white p-5">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-sm font-semibold text-text-main">
            {ROLE_LABELS[app.requestedRole] ?? app.requestedRole}
          </p>
          <p className="text-xs text-text-faint mt-0.5">{app.orgName} · {app.orgType}</p>
        </div>
        <StatusBadge status={app.status} />
      </div>
      {app.purpose && <p className="mt-3 text-sm text-text-secondary line-clamp-2">{app.purpose}</p>}
      {app.rejectionReason && (
        <div className="mt-3 rounded-lg bg-red-50 px-3 py-2 text-xs text-red-700">
          <span className="font-semibold">Rejected: </span>{app.rejectionReason}
        </div>
      )}
      <p className="mt-3 text-[11px] text-text-faint">
        Submitted {new Date(app.createdAt).toLocaleDateString()}
      </p>
    </div>
  );
}

export default function RoleApplicationPage() {
  const { user } = useAuth();
  const { data: applications, loading, refetch } = useMyRoleApplications();

  const [form, setForm] = useState({
    requestedRole: 'issuer',
    orgName: '',
    orgType: 'Company',
    website: '',
    purpose: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const hasPendingOrApproved = applications?.some(
    (a) => a.requestedRole === form.requestedRole && (a.status === 'pending' || a.status === 'approved')
  );

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await apiFetch('/auth/role-applications', {
        method: 'POST',
        body: JSON.stringify(form),
      });
      setSuccess(true);
      refetch();
      setForm({ requestedRole: 'issuer', orgName: '', orgType: 'Company', website: '', purpose: '' });
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Submission failed');
    } finally {
      setSubmitting(false);
    }
  };

  const isHolder = user?.role === 'holder';

  return (
    <div className="mx-auto max-w-2xl space-y-8 py-8 px-4">
      <div>
        <h1 className="text-2xl font-bold text-text-main">Apply for Role Upgrade</h1>
        <p className="mt-1 text-sm text-text-secondary">
          Submit an application to become a Credential Issuer or Verifier on the IDA platform.
          An admin will review your application and issue a trust credential upon approval.
        </p>
      </div>

      {/* Existing applications */}
      {!loading && applications && applications.length > 0 && (
        <section>
          <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-text-faint">Your Applications</h2>
          <div className="space-y-3">
            {applications.map((a) => <ApplicationCard key={a.id} app={a} />)}
          </div>
        </section>
      )}

      {/* Form — only for holders */}
      {!isHolder ? (
        <div className="rounded-xl border border-border-subtle bg-slate-50 p-6 text-center">
          <span className="material-symbols-outlined text-3xl text-text-faint">verified_user</span>
          <p className="mt-2 text-sm font-semibold text-text-main">You already have the role: {user?.role}</p>
          <p className="mt-1 text-xs text-text-faint">Role upgrades are only available to holders.</p>
        </div>
      ) : (
        <section>
          <h2 className="mb-4 text-sm font-semibold uppercase tracking-wide text-text-faint">New Application</h2>
          {success && (
            <div className="mb-4 flex items-center gap-2 rounded-lg bg-green-50 px-4 py-3 text-sm text-green-800">
              <span className="material-symbols-outlined text-base">check_circle</span>
              Application submitted. We will review it and notify you by email.
            </div>
          )}
          {error && (
            <div className="mb-4 rounded-lg bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>
          )}
          <form onSubmit={handleSubmit} className="space-y-4 rounded-xl border border-border-subtle bg-white p-6">
            {/* Role selector */}
            <div>
              <label className="block text-sm font-medium text-text-main mb-1">Requested Role</label>
              <div className="grid grid-cols-2 gap-3">
                {(['issuer', 'verifier'] as const).map((role) => (
                  <button
                    key={role}
                    type="button"
                    onClick={() => setForm({ ...form, requestedRole: role })}
                    className={`rounded-xl border p-4 text-left transition ${
                      form.requestedRole === role
                        ? 'border-indigo-500 bg-indigo-50'
                        : 'border-border-subtle hover:border-indigo-300'
                    }`}
                  >
                    <span className="material-symbols-outlined text-xl text-indigo-600">
                      {role === 'issuer' ? 'verified' : 'fact_check'}
                    </span>
                    <p className="mt-1 text-sm font-semibold text-text-main">{ROLE_LABELS[role]}</p>
                    <p className="text-[11px] text-text-faint">
                      {role === 'issuer'
                        ? 'Issue verifiable credentials to subjects'
                        : 'Request and verify credential presentations'}
                    </p>
                  </button>
                ))}
              </div>
              {hasPendingOrApproved && (
                <p className="mt-2 text-xs text-amber-700 bg-amber-50 rounded px-3 py-1.5">
                  You already have an active application for this role.
                </p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-text-main mb-1">Organisation Name <span className="text-red-500">*</span></label>
              <input
                type="text"
                required
                value={form.orgName}
                onChange={(e) => setForm({ ...form, orgName: e.target.value })}
                className="input w-full"
                placeholder="e.g. Acme Corp"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-text-main mb-1">Organisation Type <span className="text-red-500">*</span></label>
              <select
                value={form.orgType}
                onChange={(e) => setForm({ ...form, orgType: e.target.value })}
                className="input w-full"
              >
                {ORG_TYPES.map((t) => <option key={t}>{t}</option>)}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-text-main mb-1">Website</label>
              <input
                type="url"
                value={form.website}
                onChange={(e) => setForm({ ...form, website: e.target.value })}
                className="input w-full"
                placeholder="https://example.com"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-text-main mb-1">Purpose / Use Case <span className="text-red-500">*</span></label>
              <textarea
                required
                rows={4}
                value={form.purpose}
                onChange={(e) => setForm({ ...form, purpose: e.target.value })}
                className="input w-full resize-none"
                placeholder="Describe how you intend to use this role on the IDA platform..."
              />
            </div>

            <button
              type="submit"
              disabled={submitting || !!hasPendingOrApproved}
              className="btn-primary w-full disabled:opacity-50"
            >
              {submitting ? 'Submitting…' : 'Submit Application'}
            </button>
          </form>
        </section>
      )}
    </div>
  );
}
